<script lang="ts" setup>
import { useUiStore } from '@/store/uiStore';

const uiStore = useUiStore();
const props = withDefaults(
  defineProps<{
    title: string;
    isSidebar?: boolean;
    isWide?: boolean;
    subtitle?: string | string[];
    variant?: 'success' | 'danger';
    maxHeightMargin?: number;
    isOverflow?: boolean;
    mainOverflow?: 'hidden';
    /**
     * Scroll the body instead of the whole modal, keeping the header and footer
     * (i.e. the action buttons) visible on a short viewport. Opt-in: without it
     * a tall form overflows the modal's max-height and pushes the buttons out
     * of view entirely.
     */
    scrollBody?: boolean;
    size?: 'small' | 'medium' | 'large';
    hasAiBorder?: boolean;
    closable?: boolean;
  }>(),
  {
    variant: 'success',
    subtitle: undefined,
    maxHeightMargin: 40,
    isOverflow: true,
    mainOverflow: undefined,
    scrollBody: false,
    size: 'medium',
    hasAiBorder: false,
    closable: true,
  },
);

const slots = useSlots();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'action'): void;
}>();

uiStore.addKeymap({ Escape: () => emit('close') });

function getSubtitle() {
  if (!props.subtitle) {
    return '';
  }

  return typeof props.subtitle === 'string' ? [props.subtitle] : props.subtitle;
}
</script>

<template>
  <div
    class="ui-modal"
    :class="{
      'is-popup': !props.isSidebar,
      'is-sidebar': props.isSidebar,
      'is-wide': props.isWide,
      'is-overflow': props.isOverflow,
      'is-small': props.size === 'small',
      'is-large': props.size === 'large',
      'has-ai-border': props.hasAiBorder,
    }"
    :style="{ maxHeight: `calc(100vh - ${props.maxHeightMargin}px)` }"
  >
    <div class="top-actions">
      <slot name="topActions" />
    </div>
    <slot name="header">
      <header
        class="modal-header"
        :class="{ 'reduced-padding-top': !!slots.topActions }"
      >
        <span class="caption">
          <span
            v-if="slots.icon"
            class="title-icon"
            :class="`title-icon--${props.variant}`"
          >
            <slot name="icon" />
          </span>

          <div>
            <slot name="title">
              <h2 class="title">{{ props.title }}</h2>
            </slot>

            <p
              v-for="(t, index) in getSubtitle()"
              :key="index"
              class="modal-sub-title"
            >
              {{ t }}
            </p>
          </div>
        </span>

        <UiButton
          v-if="props.closable"
          variant="flat"
          class="close-icon-button"
          @click="emit('close')"
        >
          <UiIcon name="XIcon" class="close-icon" />
        </UiButton>
      </header>
    </slot>

    <div
      class="modal-main"
      :class="{ 'is-scrollable': props.scrollBody }"
      :style="{
        overflow: mainOverflow,
      }"
    >
      <slot />
    </div>

    <div v-if="slots.footer" class="footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@property --angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@keyframes ai-border-rotate {
  to {
    --angle: 360deg;
  }
}

.ui-modal {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 36rem;
  min-height: px(15);
  background: #fff;
  transition: all 0.5s var(--ease-standard);
  box-shadow: var(--shadow-xl);
  padding: var(--space-24);
  z-index: 10;

  &.is-small {
    width: 26rem;
  }

  &.is-overflow {
    overflow-y: visible;
  }

  &.is-popup {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: var(--border-radius-m);
  }

  &.is-wide,
  &.is-large {
    width: 59rem;
  }

  &.is-sidebar {
    right: 0;
    top: 0;
    width: 30rem;
    height: 100%;
    transform: translate(0%, 0%);
    border-radius: px(0) px(0) px(0) var(--border-radius-14);
    overflow-y: auto;
  }

  &.has-ai-border {
    border: 3px solid transparent;
    background:
      linear-gradient(#fff, #fff) padding-box,
      conic-gradient(
          from var(--angle),
          #3b82f6,
          #6366f1,
          #8b5cf6,
          #a855f7,
          #3b82f6
        )
        border-box;
    animation:
      ai-border-rotate 4s linear infinite,
      ai-border-glow 3s ease-in-out infinite;
  }
}

@keyframes ai-border-glow {
  0% {
    box-shadow:
      0px 20px 24px -4px rgba(16, 24, 40, 0.1),
      0px 8px 8px -4px rgba(16, 24, 40, 0.04),
      0 0 10px rgba(59, 130, 246, 0.3);
  }
  25% {
    box-shadow:
      0px 20px 24px -4px rgba(16, 24, 40, 0.1),
      0px 8px 8px -4px rgba(16, 24, 40, 0.04),
      0 0 15px rgba(99, 102, 241, 0.4);
  }
  50% {
    box-shadow:
      0px 20px 24px -4px rgba(16, 24, 40, 0.1),
      0px 8px 8px -4px rgba(16, 24, 40, 0.04),
      0 0 20px rgba(139, 92, 246, 0.5);
  }
  75% {
    box-shadow:
      0px 20px 24px -4px rgba(16, 24, 40, 0.1),
      0px 8px 8px -4px rgba(16, 24, 40, 0.04),
      0 0 15px rgba(168, 85, 247, 0.4);
  }
  100% {
    box-shadow:
      0px 20px 24px -4px rgba(16, 24, 40, 0.1),
      0px 8px 8px -4px rgba(16, 24, 40, 0.04),
      0 0 10px rgba(59, 130, 246, 0.3);
  }
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  &.reduced-padding-top {
    padding-top: var(--space-8);
  }
  p {
    margin-top: var(--space-8);
  }

  .title {
    font-size: var(--font-size-20);
    line-height: var(--line-height-30);
    color: #101828;
    font-weight: var(--font-weight-medium);
  }
}

.caption {
  display: flex;
  align-items: center;
  gap: var(--space-16);
}

.title-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  width: px(48);
  height: px(48);

  &--success {
    border: 0.4rem solid #dff6f4;
    color: #2e988d;
    background-color: #b0e8e3;
  }
}

.title-icon span {
  width: 1.2rem;
  height: 1.2rem;
}

.modal-sub-title {
  font-family: var(--title-font-family);
  color: var(--color-gray-500);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-14);
  line-height: var(--line-height-20);
}

.modal-main {
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  // A flex child does not shrink below its content unless min-height is reset,
  // so without this the body ignores the modal's max-height and overflows.
  &.is-scrollable {
    min-height: 0;
    overflow-y: auto;
    @include focus-ring-room;
  }
}

.footer {
  display: flex;
  gap: var(--space-16);
  justify-content: flex-end;
  padding-top: var(--space-24);

  button {
    margin-bottom: 0;
  }
}

.is-sidebar .footer {
  border-top: 2px solid var(--color-gray-50);
}
.is-sidebar .modal-main {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
  @include focus-ring-room;
}

.close-icon-button {
  position: absolute;
  top: 15px;
  right: 15px;
}
.close-icon {
  height: 1.5rem;
  width: 1.5rem;
  color: var(--color-gray-500);
  cursor: pointer;
}

@media screen and (max-height: 970px) {
  .ui-modal {
    position: fixed;

    &.is-overflow {
      overflow-y: auto;
    }
  }
}
</style>
